import Router, { useRouter } from 'next/router'
import { useCallback } from 'react'

import {
  DistanceUnit,
  Place,
  PlaceType,
  ResidentialPropertyListingConnectionEdge,
} from '@unreserved-frontend-v2/api/generated/graphql/types'
import { getBoundsForListings } from '@unreserved-frontend-v2/geolocation/api-mappings/listings'
import {
  DEFAULT_ADDRESS_SEARCH_RADIUS,
  DEFAULT_MAP_BOUNDS_CANADA,
} from '@unreserved-frontend-v2/geolocation/components/map/constants'
import { getBoundsForPointAndDistance } from '@unreserved-frontend-v2/geolocation/components/map/utils-lite'
import { GeoInputForUI } from '@unreserved-frontend-v2/geolocation/types'
import { buildQueryStringFromFilters } from '@unreserved-frontend-v2/modules/listings/utils/routing/build-query-string-from-filters'
import { FilterSelections } from '@unreserved-frontend-v2/ui/schema-table/models'

import { ListDisplayType } from './types'

export function useToggleDisplayType(
  filterSelections: FilterSelections,
  listings?: ResidentialPropertyListingConnectionEdge[] | null,
  place?: Place | null
) {
  const { query } = useRouter()

  const toggleDisplayType = useCallback(
    (type: ListDisplayType) => {
      const newSelections = { ...filterSelections }
      const noSlug = !query.listing

      //
      // Switching to Preview mode.
      //
      if ([ListDisplayType.Preview, ListDisplayType.PreviewGrid].includes(type)) {
        const url = buildQueryStringFromFilters(newSelections, Router.pathname, noSlug, {
          ...query,
          display: type,
        })
        Router.push(url)
      }
      //
      // Switching to Map mode.
      //
      else {
        const newQuery = { ...query }
        newQuery['display'] = ListDisplayType.Map
        delete newQuery['page']

        // The map will size/position itself based on the bounds in the URL. So if we don't have an existing
        // bounds filter selection, calculate one based on the current state of the filters.
        if (!newSelections['bounds']) {
          // If an Address is selected, set the search radius to 1KM and update the map bounds as well.
          if (newSelections['geo']) {
            const geoInput = newSelections['geo'] as GeoInputForUI
            const { longitude, latitude } = geoInput
            geoInput.distance = {
              unit: geoInput.distance?.unit || DistanceUnit.Kilometers,
              value: DEFAULT_ADDRESS_SEARCH_RADIUS,
            }
            newSelections['bounds'] = getBoundsForPointAndDistance(longitude, latitude, DEFAULT_ADDRESS_SEARCH_RADIUS)
          }
          // Now check for a place. There should always be a default (e.g. - canada) but check anyway.
          else if (place) {
            switch (place.type) {
              case PlaceType.Country: {
                // The user hasn't specifically chosen a Place, so use the default country bounds.
                newSelections['bounds'] = DEFAULT_MAP_BOUNDS_CANADA
                break
              }
              case PlaceType.Street: {
                // Streets only have a point for their geoJSON so use the current set of listings to set a map bounds.
                newSelections['bounds'] = getBoundsForListings(listings)
                break
              }
              default: {
                // For all other place types, we load in the geoJson after clicking on the map toggle
                break
              }
            }
          }
        }

        const url = buildQueryStringFromFilters(newSelections, Router.pathname, noSlug, newQuery)
        Router.push(url)
      }
    },
    [filterSelections, query, place, listings]
  )

  return { toggleDisplayType }
}
